import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { HiArrowUpRight } from "react-icons/hi2";
import Logo from "../assets/images/Logo.png";
import { signInWithCredentials } from "../firebase_auth/firebase_auth";
import { useNavigate } from "react-router-dom";
import StampButton from "../components/ui/StampButton";
import { useAuth } from "../hooks/auth_context";

type FormFields = {
  email: string;
  password: string;
};

export const Login = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    defaultValues: { email: "", password: "" },
  });
  const navigate = useNavigate();
  const { isLoggedIn, isLoading, currentUser } = useAuth();

  // Redirect when auth state confirms admin is logged in (avoids race condition
  // where navigate("/") would fire before onAuthStateChanged updates typeUser).
  useEffect(() => {
    if (!isLoading && isLoggedIn && currentUser.typeUser === "admin") {
      navigate("/", { replace: true });
    }
  }, [isLoggedIn, isLoading, currentUser.typeUser, navigate]);

  const onSubmit = async (data: FormFields) => {
    try {
      await signInWithCredentials(data.email, data.password);
      // Do NOT navigate here — let the useEffect above handle it once
      // onAuthStateChanged has finished fetching the user record from the backend.
    } catch (e) {
      setError("root", { message: "Invalid email / password" });
    }
  };

  return (
    <section className="min-h-screen bg-[#F5F5F5] font-sans text-[#1A1A1A]">
      <div className="flex flex-col items-center justify-center px-6 py-12 mx-auto min-h-screen">
        <div className="flex items-center mb-6">
          <img className="w-16 h-16" src={Logo} alt="TieUp Admin" />
        </div>
        <div className="w-full bg-white border border-[#E0E0E0] rounded-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.06)] sm:max-w-md">
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center">
              <StampButton as="div" label="Admin Sign In" />
              <p className="text-sm text-[#666666] mt-4">
                Sign in to manage your platform
              </p>
            </div>
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="email"
                  className="block mb-1.5 text-[13px] font-medium text-[#374151]"
                >
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  className="bg-white border border-[#D1D5DB] text-[#1A1A1A] text-sm rounded-md focus:outline-none focus:border-[#16729A] block w-full p-3 placeholder-[#9CA3AF]"
                  placeholder="name@company.com"
                  {...register("email", {
                    required: "Please type your email",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors.email?.message && (
                  <p className="text-[#B51D28] text-xs mt-1">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block mb-1.5 text-[13px] font-medium text-[#374151]"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  placeholder="Enter your password"
                  className="bg-white border border-[#D1D5DB] text-[#1A1A1A] text-sm rounded-md focus:outline-none focus:border-[#16729A] block w-full p-3 placeholder-[#9CA3AF]"
                  {...register("password", {
                    required: "Please type your password",
                  })}
                />
                {errors.password?.message && (
                  <p className="text-[#B51D28] text-xs mt-1">{errors.password.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 font-semibold rounded-lg text-sm uppercase px-5 py-3 transition-opacity disabled:opacity-50"
                style={{ background: "#16729A" }}
              >
                {isSubmitting ? "Signing in..." : (<>Log In <HiArrowUpRight size={14} /></>)}
              </button>
              {errors.root?.message && (
                <p className="text-[#B51D28] text-sm text-center">{errors.root.message}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
